import type { CourseCertificateUploadBatchResponse } from "../models/courseCertificate";
import type { CourseCertificateUploadError } from "../models/courseCertificate";
import {
  CHUNK_CONCURRENCY,
  CHUNK_RETRY_ATTEMPTS,
  CHUNK_TIMEOUT_MS,
} from "../constants/courseCertificateUpload";
import {
  chunkFailureToUploadErrors,
  remapChunkErrors,
} from "./mergeUploadErrors";
import type { UploadChunk } from "./planCourseCertificateUpload";
import { runConcurrentPool } from "./runConcurrentPool";
import { runWithRetry } from "./uploadRetry";

export type ChunkOutcome = {
  errors: CourseCertificateUploadError[];
  receivedCount: number;
};

export type ChunkSettlement = {
  settledCount: number;
  outcome: ChunkOutcome;
};

type UploadChunkRequest = (
  files: File[],
  options: { signal: AbortSignal; timeoutMs: number },
) => Promise<CourseCertificateUploadBatchResponse>;

type RunCourseCertificateUploadOptions = {
  chunks: readonly UploadChunk<File>[];
  /** Called as each chunk resolves, so the caller can reveal the new rows. */
  onChunkSettled: (settlement: ChunkSettlement) => void;
  signal: AbortSignal;
  upload: UploadChunkRequest;
};

/** Sends the planned chunks concurrently, retrying transient failures, and
 * reports each chunk's outcome as it lands. Never rejects: a chunk that fails
 * for good becomes UPLOAD_FAILED rows for its files.
 *
 * Kept free of React so it can be tested directly. */
export const runCourseCertificateUpload = async ({
  chunks,
  onChunkSettled,
  signal,
  upload,
}: RunCourseCertificateUploadOptions): Promise<void> => {
  await runConcurrentPool<UploadChunk<File>, ChunkOutcome>({
    concurrency: CHUNK_CONCURRENCY,
    items: chunks,
    signal,
    run: async (chunk) => {
      const response = await runWithRetry(
        () =>
          upload(
            chunk.entries.map((entry) => entry.file),
            { signal, timeoutMs: CHUNK_TIMEOUT_MS },
          ),
        { attempts: CHUNK_RETRY_ATTEMPTS, signal },
      );

      return {
        errors: remapChunkErrors(chunk, response.errors),
        receivedCount: response.documents.length,
      };
    },
    onSettled: (settlement) => {
      const chunk = chunks[settlement.index];

      // A chunk aborted in flight may still have committed on the server, so
      // it is neither a settled success nor a reportable failure. The cancel
      // banner says as much.
      if (settlement.status === "rejected" && signal.aborted) {
        return;
      }

      onChunkSettled({
        settledCount: chunk.entries.length,
        outcome:
          settlement.status === "fulfilled"
            ? settlement.value
            : {
                errors: chunkFailureToUploadErrors(chunk),
                receivedCount: 0,
              },
      });
    },
  });
};
